/**
 * KOPS-685: AI Supervision Tab — Unified monitoring of ALL AI communications
 *
 * Monitors:
 *   1. Ask the Broker — agent conversations with broker AI assistant
 *   2. Client Portal Chat — client questions + AI responses (both sides)
 *   3. AI Deal Briefs — AI-generated deal summaries
 *   4. Document AI Analysis — compliance scoring results
 *
 * ADRE compliance: broker must supervise all AI client communications.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faUser, faRobot, faSpinner, faEye, faComments,
  faExclamationTriangle, faCheckCircle, faFileAlt, faFilter,
  faSyncAlt, faArrowRight, faClock, faHome,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import SplitPanel from '../broker-intelligence/shared/SplitPanel';
import SearchBar from '../broker-intelligence/shared/SearchBar';
import ListItem from '../broker-intelligence/shared/ListItem';
import { colors, fonts } from '../broker-intelligence/shared/colors';

// ---------------------------------------------------------------------------
// Type config
// ---------------------------------------------------------------------------
const TYPE_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  broker_chat:          { icon: faComments,          color: '#3498db', label: 'Ask the Broker' },
  client_question:      { icon: faUser,              color: '#e67e22', label: 'Client Question' },
  ai_client_response:   { icon: faRobot,             color: '#8e44ad', label: 'AI → Client' },
  client_portal_visit:  { icon: faEye,               color: '#27ae60', label: 'Portal Visit' },
  client_brief_view:    { icon: faFileAlt,            color: '#27ae60', label: 'Viewed Update' },
  deal_brief:           { icon: faRobot,             color: '#C9952A', label: 'AI Deal Brief' },
  doc_analysis:         { icon: faShieldAlt,          color: '#2980b9', label: 'Doc Analysis' },
  website_chat:         { icon: faHome,              color: '#16a085', label: 'Website Chat' },
};

const FILTER_OPTIONS = [
  { key: 'all', label: 'All AI Activity' },
  { key: 'broker_chat', label: 'Ask the Broker' },
  { key: 'client_chat', label: 'Client Portal' },
  { key: 'brief', label: 'Deal Briefs' },
  { key: 'doc_analysis', label: 'Doc Analysis' },
  { key: 'website_chat', label: 'Website Chat' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AISupervisionTab: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [detailMessages, setDetailMessages] = useState<any[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/broker/ai-supervision/feed', {
        params: { type: filterType, limit: 80 },
      });
      if (res.data.success) {
        setItems(res.data.items || []);
        setSummary(res.data.summary || null);
      }
    } catch (err) {
      console.error('Failed to load AI supervision feed:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // Load conversation detail when item selected
  const viewDetail = useCallback(async (item: any) => {
    setSelectedItem(item);
    setDetailMessages([]);

    if (item.type === 'broker_chat' && item.conversationId) {
      setLoadingDetail(true);
      try {
        const res = await axiosInstance.get(`/broker/ai-supervision/conversation/${item.conversationId}/messages`);
        setDetailMessages(res.data.messages || []);
      } catch {}
      setLoadingDetail(false);
    } else if (item.type === 'website_chat' && item.conversationId) {
      setLoadingDetail(true);
      try {
        const res = await axiosInstance.get(`/broker/ai-supervision/website-chat/${item.conversationId}`);
        setDetailMessages(res.data.messages || []);
      } catch {}
      setLoadingDetail(false);
    } else if (item.type === 'client_question' || item.type === 'ai_client_response') {
      setLoadingDetail(true);
      try {
        const res = await axiosInstance.get(`/broker/ai-supervision/client-chat/${item.dealId}`);
        setDetailMessages(res.data.messages || []);
      } catch {}
      setLoadingDetail(false);
    }
    // For briefs and doc analysis, the description is enough — no conversation to load
  }, []);

  // Filter by search
  const filteredItems = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.agentName || '').toLowerCase().includes(q) ||
      (i.title || '').toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q) ||
      (i.clientName || '').toLowerCase().includes(q) ||
      (i.propertyAddress || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <SplitPanel
      leftWidth={420}
      left={
        <>
          {/* Header */}
          <div style={{ padding: '16px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ color: colors.gold }} />
              <span style={{ color: colors.white, fontWeight: 700, fontSize: 15, fontFamily: fonts.body }}>
                AI Supervision
              </span>
            </div>

            {/* Stats */}
            {summary && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[
                  { label: 'Total', value: summary.totalItems, color: colors.blue },
                  { label: 'Agents', value: summary.uniqueAgents, color: colors.green },
                  { label: 'Client Chats', value: (summary.typeCounts?.client_question || 0), color: '#e67e22' },
                  { label: 'Briefs', value: (summary.typeCounts?.deal_brief || 0), color: colors.gold },
                ].map(s => (
                  <div key={s.label} style={{
                    flex: 1, background: colors.navy3, borderRadius: 6, padding: '6px',
                    textAlign: 'center',
                  }}>
                    <div style={{ color: s.color, fontWeight: 700, fontSize: 16, fontFamily: fonts.body }}>
                      {s.value || 0}
                    </div>
                    <div style={{ color: colors.muted, fontSize: 9, fontWeight: 500 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Filter pills */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
              {FILTER_OPTIONS.map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilterType(f.key)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, border: 'none', cursor: 'pointer',
                    fontSize: 10, fontWeight: 600,
                    background: filterType === f.key ? colors.gold : colors.navy3,
                    color: filterType === f.key ? colors.navy : colors.muted,
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search agent, client, property, topic..."
            />
          </div>

          {/* Feed list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading AI activity...
              </div>
            ) : filteredItems.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faRobot} style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                <div style={{ fontSize: 13 }}>No AI activity found</div>
              </div>
            ) : (
              filteredItems.map(item => {
                const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.broker_chat;
                return (
                  <div
                    key={item.id}
                    onClick={() => viewDetail(item)}
                    style={{
                      padding: '10px 14px', borderBottom: `1px solid ${colors.navy3}`,
                      cursor: 'pointer',
                      background: selectedItem?.id === item.id ? colors.navy3 : 'transparent',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <FontAwesomeIcon icon={cfg.icon} style={{ color: cfg.color, fontSize: 11 }} />
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                        background: `${cfg.color}22`, color: cfg.color, textTransform: 'uppercase',
                      }}>
                        {cfg.label}
                      </span>
                      <span style={{ fontSize: 10, color: colors.muted, marginLeft: 'auto' }}>
                        {formatTime(item.updatedAt || item.createdAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.white, marginBottom: 2 }}>
                      {item.agentName}
                      {item.clientName && <span style={{ color: colors.muted, fontWeight: 400 }}> — {item.clientName}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: colors.muted, lineHeight: 1.4 }}>
                      {(item.description || '').substring(0, 120)}
                    </div>
                    {item.propertyAddress && (
                      <div style={{ fontSize: 10, color: colors.gold, marginTop: 2 }}>
                        <FontAwesomeIcon icon={faHome} style={{ marginRight: 4, fontSize: 9 }} />
                        {item.propertyAddress}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      }
      right={
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {!selectedItem ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', padding: 40, textAlign: 'center',
            }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 24, boxShadow: '0 4px 20px rgba(201,149,42,0.3)',
              }}>
                <FontAwesomeIcon icon={faEye} style={{ fontSize: 36, color: colors.navy }} />
              </div>
              <h2 style={{ color: colors.navy, fontFamily: fonts.display, fontSize: 28, margin: '0 0 8px' }}>
                AI Supervision
              </h2>
              <p style={{ color: colors.muted, fontSize: 14, maxWidth: 440, lineHeight: 1.6 }}>
                Monitor all AI communications across your brokerage — agent consultations,
                client portal conversations, deal briefs, and document compliance analysis.
              </p>
              <div style={{
                background: 'rgba(201,149,42,0.1)', border: `1px solid ${colors.gold}`,
                borderRadius: 8, padding: '12px 20px', maxWidth: 440, marginTop: 24,
                fontSize: 12, color: colors.gold, lineHeight: 1.5, textAlign: 'left',
              }}>
                <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 6 }} />
                <strong>ADRE Compliance:</strong> Per ARS 32-2153(A)(21), the Designated Broker must exercise
                reasonable supervision over all communications, including AI-generated content.
                All conversations are logged with full audit trail.
              </div>
            </div>
          ) : (
            <>
              {/* Detail header */}
              <div style={{
                padding: '14px 24px', borderBottom: `1px solid ${colors.border}`,
                background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: (TYPE_CONFIG[selectedItem.type]?.color || colors.gold) + '22',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FontAwesomeIcon
                      icon={TYPE_CONFIG[selectedItem.type]?.icon || faRobot}
                      style={{ fontSize: 16, color: TYPE_CONFIG[selectedItem.type]?.color || colors.gold }}
                    />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: colors.navy, fontFamily: fonts.body }}>
                      {selectedItem.title || selectedItem.agentName}
                    </div>
                    <div style={{ fontSize: 11, color: colors.muted }}>
                      {TYPE_CONFIG[selectedItem.type]?.label || selectedItem.type}
                      {selectedItem.agentEmail && ` · ${selectedItem.agentEmail}`}
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: colors.offBg, borderRadius: 6, padding: '4px 10px',
                  fontSize: 11, color: colors.navy, fontWeight: 600,
                }}>
                  <FontAwesomeIcon icon={faEye} style={{ color: colors.gold }} />
                  Read-only
                </div>
              </div>

              {/* Detail content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                {loadingDetail ? (
                  <div style={{ textAlign: 'center', color: colors.muted, marginTop: 40 }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} /> Loading...
                  </div>
                ) : detailMessages.length > 0 ? (
                  // Conversation view (broker chat or client chat)
                  detailMessages.map((msg, idx) => {
                    const isHuman = msg.role === 'user' || msg.role === 'client';
                    return (
                      <div key={msg.id || idx} style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: isHuman ? 'flex-end' : 'flex-start',
                        marginBottom: 14,
                      }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3,
                          flexDirection: isHuman ? 'row-reverse' : 'row',
                        }}>
                          <div style={{
                            width: 26, height: 26, borderRadius: '50%',
                            background: isHuman ? colors.navy3 : `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <FontAwesomeIcon
                              icon={isHuman ? faUser : faRobot}
                              style={{ fontSize: 11, color: isHuman ? colors.muted : colors.navy }}
                            />
                          </div>
                          <span style={{ fontSize: 11, color: colors.muted, fontWeight: 600 }}>
                            {isHuman ? (msg.role === 'client' ? selectedItem.clientName || 'Client' : selectedItem.agentName || 'Agent') : 'AI Assistant'}
                          </span>
                          <span style={{ fontSize: 10, color: colors.muted }}>
                            {formatTime(msg.timestamp)}
                          </span>
                        </div>
                        <div style={{
                          maxWidth: '80%',
                          background: isHuman ? colors.navy2 : '#fff',
                          color: isHuman ? colors.white : colors.navy,
                          borderRadius: isHuman ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
                          fontFamily: fonts.body, whiteSpace: 'pre-wrap',
                          border: isHuman ? 'none' : `1px solid ${colors.border}`,
                        }}>
                          {msg.content}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  // Single-item detail (brief, doc analysis, portal visit)
                  <div>
                    {selectedItem.propertyAddress && (
                      <div style={{
                        background: '#f8f9fa', borderRadius: 8, padding: 14, marginBottom: 16,
                        border: '1px solid #e9ecef', borderLeft: `4px solid ${colors.gold}`,
                      }}>
                        <div style={{ fontSize: 11, color: colors.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Property</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: colors.navy }}>{selectedItem.propertyAddress}</div>
                        {selectedItem.clientName && <div style={{ fontSize: 12, color: colors.muted }}>Client: {selectedItem.clientName}</div>}
                      </div>
                    )}

                    {selectedItem.briefScore !== undefined && selectedItem.briefScore !== null && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
                        padding: 12, background: '#f8f9fa', borderRadius: 8,
                      }}>
                        <div style={{
                          width: 48, height: 48, borderRadius: '50%',
                          background: selectedItem.briefScore >= 80 ? '#27ae6020' : selectedItem.briefScore >= 50 ? '#f39c1220' : '#e74c3c20',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{
                            fontSize: 16, fontWeight: 700,
                            color: selectedItem.briefScore >= 80 ? '#27ae60' : selectedItem.briefScore >= 50 ? '#f39c12' : '#e74c3c',
                          }}>
                            {selectedItem.briefScore}%
                          </span>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: colors.navy }}>Compliance Score</div>
                          <div style={{ fontSize: 11, color: colors.muted }}>AI-assessed document completeness</div>
                        </div>
                      </div>
                    )}

                    <div style={{
                      fontSize: 13, color: '#333', lineHeight: 1.7,
                      whiteSpace: 'pre-wrap', fontFamily: fonts.body,
                    }}>
                      {selectedItem.description}
                    </div>

                    <div style={{ fontSize: 10, color: colors.muted, marginTop: 16 }}>
                      <FontAwesomeIcon icon={faClock} style={{ marginRight: 4 }} />
                      {new Date(selectedItem.createdAt).toLocaleString('en-US', {
                        timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '12px 24px', borderTop: `1px solid ${colors.border}`,
                background: colors.offBg, textAlign: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: 14 }} />
                <span style={{ fontSize: 12, color: colors.muted }}>
                  Supervision mode — read-only audit access
                </span>
              </div>
            </>
          )}
        </div>
      }
    />
  );
};

export default AISupervisionTab;
